import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import ls from 'localstorage-slim'
import { HelpCircle, LogOut, Moon, Sun } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Theme } from '@ankor-io/common/types/account'
import { updateThemeMode } from '@ankor-io/common/util/updateThemeMode'
import { AvatarButton } from '@ankor-io/ui-components/src/Avatar'
import {
  DropdownButton,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownMenu,
} from '@ankor-io/ui-components/src/Dropdown'
import { Dropdown } from '@ankor-io/ui-components/src/Dropdown'

import { ANKORPAY_HELP_LINK } from '@/constants/helpLinks'
import { useUserContext } from '@/providers/user/useUserContext'

// TODO
// import UserProfileModal from '@/views/components/UserProfileModal'

export const UserAvatarMenu = () => {
  // const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  // const [userAvatar, setUserAvatar] = useState('🐼') // Default avatar
  const { preferences, updatePreferences, user } = useUserContext()
  const { logout } = useKindeAuth()

  // Minimal reactive theme state: initialize from user preferences, DOM class, localStorage, and matchMedia fallbacks
  const [isDark, setIsDark] = useState(() => {
    // First check user preferences
    if (preferences?.theme === Theme.DARK) {
      return true
    }

    if (preferences?.theme === Theme.LIGHT) {
      return false
    }

    // Then check DOM class
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark')
    }

    // Fallback to stored preference when DOM class is not yet applied
    try {
      const stored = ls.get('prefers-color-scheme') as Theme | null
      if (stored === Theme.DARK) {
        return true
      }

      if (stored === Theme.LIGHT) {
        return false
      }
    } catch (error) {
      console.error('Error checking dark mode', error)
    }

    // Fallback to browser preference
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)')?.matches
  })

  // Sync isDark state when preferences.theme changes
  useEffect(() => {
    if (preferences?.theme === Theme.DARK) {
      setIsDark(true)
    } else if (preferences?.theme === Theme.LIGHT) {
      setIsDark(false)
    }
  }, [preferences?.theme])

  // Get user's full name
  const getUserFullName = () => {
    if (!user) {
      return ''
    }

    const { givenName, familyName } = user
    if (givenName && familyName) {
      return `${givenName} ${familyName}`
    }

    return givenName || familyName
  }

  return (
    <>
      <Dropdown>
        <DropdownButton
          as={AvatarButton}
          className="size-8 cursor-pointer hover:ring-2 hover:ring-white/50 transition-all"
          initials={user ? `${user?.givenName?.charAt(0)}${user?.familyName?.charAt(0)}` : ''}
          alt="Profile avatar"
          data-testid="avatar-button"
        ></DropdownButton>
        <DropdownMenu anchor="bottom end">
          <DropdownHeader className="font-medium text-gray-900 dark:text-white">{getUserFullName()}</DropdownHeader>
          <DropdownDivider />
          {/* <DropdownItem onClick={() => setIsProfileModalOpen(true)}>
            <User className="mr-2 size-4" />
            My Profile
          </DropdownItem> */}
          <DropdownItem onClick={() => window.open(ANKORPAY_HELP_LINK, '_blank', 'noopener,noreferrer')}>
            <HelpCircle className="mr-2 size-4" />
            Support
          </DropdownItem>
          {isDark ? (
            <DropdownItem
              onClick={() => {
                updateThemeMode(Theme.LIGHT)
                setIsDark(false)
                updatePreferences({ theme: Theme.LIGHT })
              }}
            >
              <Sun className="mr-2 size-4" />
              Light Mode
            </DropdownItem>
          ) : (
            <DropdownItem
              onClick={() => {
                updateThemeMode(Theme.DARK)
                setIsDark(true)
                updatePreferences({ theme: Theme.DARK })
              }}
            >
              <Moon className="mr-2 size-4" />
              Dark Mode
            </DropdownItem>
          )}
          <DropdownItem
            onClick={() => {
              // Dispatch custom event to notify ProtectedRoute that logout is starting
              window.dispatchEvent(new CustomEvent('user-logout'))
              logout()
            }}
          >
            <LogOut className="mr-2 size-4" />
            Logout
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {/* <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentAvatar={userAvatar}
        onAvatarChange={setUserAvatar}
      /> */}
    </>
  )
}

export default UserAvatarMenu
